import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { NUTRIENTS, PACK_SIZES, SERVING_SIZES, formatRange, quantityLabel } from "@/data/milletNutrition";

const RangeTable = ({ id, quantities }) => (
  <>
    <div className="hidden md:block">
      <Table data-testid={`${id}-table`} aria-label={`${id === "pack" ? "Pack size" : "Serving size"} nutritional comparison`} className="table-fixed">
        <TableHeader>
          <TableRow className="bg-ragi-subtle hover:bg-ragi-subtle">
            <TableHead scope="col" className="w-[22%] py-4 text-ragi-dark">Nutrient</TableHead>
            {quantities.map((grams) => (
              <TableHead scope="col" key={grams} className="py-4 text-ragi-dark" data-testid={`${id}-heading-${grams}`}>
                Per {quantityLabel(grams)}
                <span className="mt-1 block text-xs font-normal text-muted-foreground">{grams === 100 ? "Base values" : `${grams / 100} × base`}</span>
              </TableHead>
            ))}
          </TableRow>
        </TableHeader>
        <TableBody>
          {NUTRIENTS.map((nutrient) => (
            <TableRow key={nutrient.id}>
              <TableHead scope="row" className="py-5 font-medium text-ragi-dark">{nutrient.label}</TableHead>
              {quantities.map((grams) => <TableCell className="py-5 text-xs lg:text-sm tabular-nums" key={grams} data-testid={`${id}-${nutrient.id}-${grams}`}>{formatRange(nutrient.id, grams)}</TableCell>)}
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </div>
    <div className="md:hidden" data-testid={`${id}-mobile-comparison`}>
      {NUTRIENTS.map((nutrient) => (
        <div key={nutrient.id} className="border-b border-cream-muted py-5">
          <h3 className="mb-3 text-sm font-semibold text-ragi-dark">{nutrient.label}</h3>
          <dl className="grid grid-cols-2 gap-x-4 gap-y-3">
            {quantities.map((grams) => (
              <div key={grams} data-testid={`${id}-mobile-${nutrient.id}-${grams}`}>
                <dt className="text-xs text-muted-foreground">Per {quantityLabel(grams)}</dt>
                <dd className="mt-1 text-xs font-medium tabular-nums text-ragi-dark">{formatRange(nutrient.id, grams)}</dd>
              </div>
            ))}
          </dl>
        </div>
      ))}
    </div>
  </>
);

export const NutritionTable = () => (
  <section id="nutrition-values" className="scroll-mt-6 py-12 sm:py-16" aria-labelledby="nutrition-values-title">
    <div className="mb-7 flex flex-wrap items-end justify-between gap-4">
      <div>
        <p className="mb-2 font-mono text-xs uppercase text-terracotta">01 / The raw powder</p>
        <h2 id="nutrition-values-title" className="font-display text-base font-semibold md:text-lg" data-testid="nutrition-values-title">Nutrition, by quantity</h2>
      </div>
      <p className="text-xs text-muted-foreground" data-testid="nutrition-dry-weight-note">Dry weight · no milk, water or sweetener included</p>
    </div>
    <Tabs defaultValue="packs" data-testid="nutrition-comparison-tabs">
      <TabsList className="mb-5 h-11 bg-cream-muted/60" aria-label="Nutrition comparison type" data-testid="nutrition-tab-list">
        <TabsTrigger className="h-9" value="packs" data-testid="nutrition-packs-tab">Pack sizes</TabsTrigger>
        <TabsTrigger className="h-9" value="servings" data-testid="nutrition-servings-tab">Single servings</TabsTrigger>
      </TabsList>
      <TabsContent value="packs" data-testid="nutrition-packs-panel"><RangeTable id="pack" quantities={PACK_SIZES} /></TabsContent>
      <TabsContent value="servings" data-testid="nutrition-servings-panel">
        <p className="mb-4 text-xs leading-relaxed text-muted-foreground" data-testid="tablespoon-estimate-note">30 g ≈ 2 tbsp · 45 g ≈ 3 tbsp. Spoon weights vary; a kitchen scale gives a more reliable portion.</p>
        <RangeTable id="serving" quantities={SERVING_SIZES} />
      </TabsContent>
    </Tabs>
    <p className="mt-5 text-xs leading-relaxed text-muted-foreground" data-testid="nutrition-rounding-note">Calculated from the supplied 100 g ranges, then rounded to up to two decimal places. The extra digits reflect arithmetic, not laboratory precision.</p>
  </section>
);